import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Plus, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { BULK_OCCASION_TYPES } from "@/lib/bulkCelebrations";
import {
  addExistingToBulkRoster,
  addNewMemberToBulkRoster,
  isRosterWarningsError,
} from "@/lib/bulkRosterAdd";
import type { BulkRosterWarning } from "@/lib/bulkRosterWarnings";
import { useToast } from "@/hooks/use-toast";
import { useQueryClient } from "@tanstack/react-query";

type Props = {
  companyId: string;
  runId: string;
  periodStart: string;
  periodEnd: string;
  occasionTypes: string[];
  memberCelebrationIds: string[];
  onAdded: () => void;
};

export function BulkCelebrationRosterPanel({
  companyId,
  runId,
  periodStart,
  periodEnd,
  occasionTypes,
  memberCelebrationIds,
  onAdded,
}: Props) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [mode, setMode] = useState<"pick" | "new">("pick");
  const [busy, setBusy] = useState(false);
  const [pendingWarnings, setPendingWarnings] = useState<BulkRosterWarning[] | null>(null);
  const [selectedCelebrationId, setSelectedCelebrationId] = useState("");

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [occasionType, setOccasionType] = useState<string>(occasionTypes[0] ?? "birthday");
  const [celebrationDate, setCelebrationDate] = useState(periodStart);
  const [birthday, setBirthday] = useState("");

  const candidatesQuery = useQuery({
    queryKey: [
      "bulk-roster-candidates",
      companyId,
      periodStart,
      periodEnd,
      memberCelebrationIds.join(","),
    ],
    queryFn: async () => {
      const types = occasionTypes.length ? occasionTypes : ["birthday"];
      const { data, error } = await supabase
        .from("celebrations")
        .select("id, date, type, team_members(name)")
        .eq("company_id", companyId)
        .gte("date", periodStart)
        .lte("date", periodEnd)
        .in("type", types)
        .order("date", { ascending: true });
      if (error) throw error;
      const onRoster = new Set(memberCelebrationIds);
      return (data ?? []).filter((c) => !onRoster.has(c.id));
    },
  });

  const candidates = candidatesQuery.data ?? [];

  const runAdd = async (confirmWarnings: boolean) => {
    setBusy(true);
    try {
      if (mode === "pick") {
        if (!selectedCelebrationId) return;
        await addExistingToBulkRoster({
          runId,
          celebrationId: selectedCelebrationId,
          confirmWarnings,
        });
      } else {
        await addNewMemberToBulkRoster({
          runId,
          name: name.trim(),
          email: email.trim() || undefined,
          birthday: birthday || undefined,
          occasionType,
          celebrationDate,
          confirmWarnings,
        });
        setName("");
        setEmail("");
        setBirthday("");
      }
      setPendingWarnings(null);
      void queryClient.invalidateQueries({ queryKey: ["celebrations"] });
      void queryClient.invalidateQueries({ queryKey: ["team_members", companyId] });
      onAdded();
      toast({ title: "Added to bulk roster" });
    } catch (err) {
      if (isRosterWarningsError(err)) {
        setPendingWarnings(err.warnings);
        return;
      }
      toast({
        title: "Couldn't add to roster",
        description: err instanceof Error ? err.message : "Try again",
        variant: "destructive",
      });
    } finally {
      setBusy(false);
    }
  };

  const warningList = useMemo(() => pendingWarnings ?? [], [pendingWarnings]);

  return (
    <div className="space-y-3 rounded-lg border border-dashed border-charcoal/15 p-3">
      <div className="flex gap-2">
        <Button
          type="button"
          size="sm"
          variant={mode === "pick" ? "primary" : "outline"}
          onClick={() => setMode("pick")}
        >
          Add existing
        </Button>
        <Button
          type="button"
          size="sm"
          variant={mode === "new" ? "primary" : "outline"}
          onClick={() => setMode("new")}
        >
          Add new person
        </Button>
      </div>

      {warningList.length > 0 && (
        <div className="rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-950">
          <p className="flex items-center gap-1 font-medium">
            <AlertTriangle className="h-4 w-4" />
            Please confirm
          </p>
          <ul className="mt-1 list-disc pl-5">
            {warningList.map((w) => (
              <li key={w.code}>{w.message}</li>
            ))}
          </ul>
          <Button
            type="button"
            size="sm"
            className="mt-2"
            variant="outline"
            disabled={busy}
            onClick={() => void runAdd(true)}
          >
            Add anyway
          </Button>
        </div>
      )}

      {mode === "pick" ? (
        <div className="space-y-2">
          <Label className="text-xs">Celebration in this period</Label>
          <Select value={selectedCelebrationId} onValueChange={setSelectedCelebrationId}>
            <SelectTrigger>
              <SelectValue placeholder={candidates.length ? "Select…" : "None available"} />
            </SelectTrigger>
            <SelectContent>
              {candidates.map((c) => {
                const tm = c.team_members as { name?: string } | null;
                return (
                  <SelectItem key={c.id} value={c.id}>
                    {tm?.name ?? "Team member"} · {c.type.replace(/_/g, " ")} · {c.date}
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
          <Button
            type="button"
            size="sm"
            disabled={busy || !selectedCelebrationId}
            onClick={() => void runAdd(false)}
          >
            <Plus className="mr-1 h-3 w-3" />
            Add to roster
          </Button>
        </div>
      ) : (
        <div className="grid gap-2 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <Label className="text-xs">Full name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Jesse Lee" />
          </div>
          <div>
            <Label className="text-xs">Email (optional)</Label>
            <Input value={email} onChange={(e) => setEmail(e.target.value)} type="email" />
          </div>
          <div>
            <Label className="text-xs">Birthday (optional)</Label>
            <Input value={birthday} onChange={(e) => setBirthday(e.target.value)} type="date" />
          </div>
          <div>
            <Label className="text-xs">Occasion</Label>
            <Select value={occasionType} onValueChange={setOccasionType}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {BULK_OCCASION_TYPES.filter((t) => occasionTypes.includes(t)).map((t) => (
                  <SelectItem key={t} value={t}>
                    {t.replace(/_/g, " ")}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Celebration date (in period)</Label>
            <Input
              value={celebrationDate}
              onChange={(e) => setCelebrationDate(e.target.value)}
              type="date"
              min={periodStart}
              max={periodEnd}
            />
          </div>
          <div className="sm:col-span-2">
            <Button
              type="button"
              size="sm"
              disabled={busy || name.trim().length < 2}
              onClick={() => void runAdd(false)}
            >
              <Plus className="mr-1 h-3 w-3" />
              Create & add to roster
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
